import {useCallback, useEffect, useMemo, useState} from 'react';

const STORAGE_KEY = 'ticketList.collapsedGroups.v1';

function readPersisted(): Set<string> {
	if (typeof window === 'undefined') return new Set();
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) return new Set();
		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed)) return new Set();
		return new Set(parsed.filter((k): k is string => typeof k === 'string'));
	} catch {
		return new Set();
	}
}

function writePersisted(collapsed: ReadonlySet<string>) {
	if (typeof window === 'undefined') return;
	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...collapsed]));
	} catch {
		// localStorage may be disabled or quota-exceeded; silently ignore.
	}
}

export interface UseGroupCollapse {
	isCollapsed: (status: string) => boolean;
	toggle: (status: string) => void;
}

export function useGroupCollapse(): UseGroupCollapse {
	const [collapsed, setCollapsed] = useState<Set<string>>(() => readPersisted());

	useEffect(() => {
		writePersisted(collapsed);
	}, [collapsed]);

	const toggle = useCallback((status: string) => {
		setCollapsed(prev => {
			const next = new Set(prev);
			if (next.has(status)) next.delete(status);
			else next.add(status);
			return next;
		});
	}, []);

	const isCollapsed = useCallback((status: string) => collapsed.has(status), [collapsed]);

	return useMemo(() => ({isCollapsed, toggle}), [isCollapsed, toggle]);
}
